import { Component, OnInit, AfterViewInit, ElementRef, ViewChild, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';
import { ScrollTrigger, CSSPlugin } from 'gsap/all';

let popularPluginsRegistered = false;

interface EventItem {
  id: string;
  title: string;
  description?: string;
  location: string;
  date: string;
  price: string;
  image: string;
  type: string;
  instructor?: string;
  duration?: string;
  attendees?: number;
  rating?: number;
}

@Component({
  selector: 'app-popular-events',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './popular-events.component.html',
  styleUrl: './popular-events.component.scss'
})
export class PopularEventsComponent implements OnInit, AfterViewInit {
  @ViewChild('popularSection', { static: true }) popularSection!: ElementRef<HTMLElement>;

  private readonly isBrowser: boolean;

  popularEvents: EventItem[] = [];
  loading = false;
  error = '';

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser && !popularPluginsRegistered) {
      gsap.registerPlugin(ScrollTrigger, CSSPlugin);
      popularPluginsRegistered = true;
    }
  }

  ngOnInit(): void {
    this.loadPopularEvents();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    if (window.innerWidth <= 768) {
      return;
    }

    this.initializeAnimations();
  }

  async loadPopularEvents(): Promise<void> {
    this.loading = true;
    this.error = '';

    try {
      this.popularEvents = [
        {
          id: '5',
          title: 'Advanced Contemporary Workshop',
          description: 'Master the art of contemporary dance with professional choreography',
          location: 'Elite Dance Academy',
          date: 'Next Week',
          price: '$75',
          image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=500&h=350&fit=crop',
          type: 'Workshop',
          instructor: 'Sarah Johnson',
          duration: '3 hours',
          attendees: 45,
          rating: 4.8
        },
        {
          id: '6',
          title: 'Street Dance Battle',
          description: 'Join the ultimate street dance competition',
          location: 'Street Dance Arena',
          date: 'This Weekend',
          price: '$50',
          image: 'https://images.unsplash.com/photo-1504609773096-104ff2c73ba4?w=500&h=350&fit=crop',
          type: 'Competition',
          instructor: 'Mike Chen',
          duration: '4 hours',
          attendees: 120,
          rating: 4.9
        },
        {
          id: '7',
          title: 'Ballet Masterclass',
          description: 'Perfect your technique with world-class instruction',
          location: 'Classical Ballet School',
          date: 'Next Month',
          price: '$100',
          image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=500&h=350&fit=crop',
          type: 'Masterclass',
          instructor: 'Elena Rodriguez',
          duration: '2 hours',
          attendees: 25,
          rating: 4.9
        }
      ];
    } catch (error) {
      this.error = 'Failed to load popular events. Please try again.';
      console.error('Error loading popular events:', error);
    } finally {
      this.loading = false;
    }
  }

  initializeAnimations(): void {
    if (!this.isBrowser) {
      return;
    }

    this.animateSectionOnScroll(this.popularSection.nativeElement, 0.4);
  }

  animateSectionOnScroll(element: HTMLElement, delay = 0): void {
    if (!this.isBrowser) {
      return;
    }

    gsap.fromTo(
      element,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        delay,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 80%',
          end: 'bottom 20%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    const cards = element.querySelectorAll('.event-card-large');
    gsap.fromTo(
      cards,
      { y: 30, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        delay: delay + 0.2,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 70%',
          end: 'bottom 30%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  }

  onEventClick(event: EventItem): void {
    if (!this.isBrowser) {
      return;
    }

  }

  getStarRating(rating: number): string[] {
    const stars: string[] = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push('★');
    }

    if (hasHalfStar) {
      stars.push('☆');
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push('☆');
    }

    return stars;
  }
}
